import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "@/lib/api";
import { Card, DemoBadge } from "@/components/ui-kit";
import { Loader } from "@/components/Loader";
import { ArrowLeft, MapPin, Users, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function CommunityPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({ name: "", pin_code: "", city: "", state: "", address_landmark: "" });

  const load = () => api.get("/users/me/community").then((r) => {
    setState(r.data);
    if (r.data.community) setForm({ name: r.data.community.name, pin_code: r.data.community.pin_code, city: r.data.community.city, state: r.data.community.state, address_landmark: r.data.address_landmark || "" });
  }).catch(() => setState({}));
  useEffect(() => { load(); }, []);

  const search = async (q) => { try { const { data } = await api.get(`/communities?search=${encodeURIComponent(q)}`); setResults(data); } catch { setResults([]); } };

  const save = async () => {
    if (!form.name || !form.pin_code) { toast.error("Locality name and PIN code required"); return; }
    setBusy(true);
    try { await api.post("/users/me/home-community", form); toast.success("Home community updated"); setEditing(false); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!state) return <Loader />;
  const c = state.community;

  return (
    <div className="ll-fade-up" data-testid="community-screen">
      <div className="px-5 pt-8 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/profile")} data-testid="cp-back" className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold font-head tracking-tight">Home community</h1>
      </div>

      <div className="px-5 mt-3">
        {c && !editing && (
          <Card data-testid="cp-current">
            <div className="flex items-center justify-between mb-2"><span className="text-xs font-bold uppercase tracking-wider text-[#111827]/50 flex items-center gap-1"><MapPin className="w-4 h-4" /> Your community</span><DemoBadge /></div>
            <p className="text-2xl font-bold">{c.name}</p>
            <p className="text-[#111827]/60">{c.city}, {c.state} · {c.pin_code}</p>
            <p className="text-[#111827]/50 text-sm mt-1">{state.address_landmark}</p>
            <div className="flex items-center gap-2 mt-3 text-sm font-semibold text-[#F97316]"><ShieldCheck className="w-4 h-4" /> <span className="capitalize">{c.status} community</span></div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-[#FFF7E6] rounded-2xl p-4 text-center"><p className="text-2xl font-black">{c.demo_residents ?? 0}</p><p className="text-xs text-[#111827]/60 font-semibold">Residents</p></div>
              <div className="bg-[#FFF7E6] rounded-2xl p-4 text-center"><p className="text-2xl font-black">{c.demo_responders ?? 0}</p><p className="text-xs text-[#111827]/60 font-semibold">Responders</p></div>
            </div>
            <button data-testid="cp-change" onClick={() => setEditing(true)} className="w-full mt-4 bg-[#C4B5FD]/20 text-[#111827] rounded-2xl py-3 font-semibold">Change community</button>
          </Card>
        )}

        {(editing || !c) && (
          <Card>
            <p className="text-sm text-[#111827]/70 bg-[#C4B5FD]/15 rounded-xl p-3 mb-4">Your home community is where you live. Your live location, when explicitly shared during an emergency, may be different.</p>
            <input data-testid="cp-search" placeholder="Search locality" onChange={(e) => search(e.target.value)} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316] mb-2" />
            {results.length > 0 && (
              <div className="bg-white rounded-xl border border-[#111827]/10 mb-3 overflow-hidden">
                {results.map((r) => <button key={r.id} data-testid={`cp-result-${r.id}`} onClick={() => { setForm({ ...form, name: r.name, pin_code: r.pin_code, city: r.city, state: r.state }); setResults([]); }} className="w-full text-left px-4 py-3 border-b border-[#111827]/5 last:border-0 hover:bg-[#C4B5FD]/10"><span className="font-semibold">{r.name}</span> <span className="text-sm text-[#111827]/50">· {r.city}, {r.pin_code}</span></button>)}
              </div>
            )}
            <div className="space-y-3">
              <input data-testid="cp-name" placeholder="Locality name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316]" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="cp-pin" placeholder="PIN code" value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 outline-none focus:border-[#F97316]" />
                <input data-testid="cp-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 outline-none focus:border-[#F97316]" />
              </div>
              <input data-testid="cp-state" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316]" />
              <input data-testid="cp-landmark" placeholder="Address or landmark" value={form.address_landmark} onChange={(e) => setForm({ ...form, address_landmark: e.target.value })} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-4 w-full outline-none focus:border-[#F97316]" />
              <button data-testid="cp-save" onClick={save} disabled={busy} className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold min-h-[56px] flex items-center justify-center">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirm community"}</button>
              {c && <button onClick={() => setEditing(false)} className="w-full text-[#111827]/60 font-semibold py-2">Cancel</button>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
